// File: src/features/coach/components/CoachProfileCompletionForm.tsx
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { CoachProfileUpdatePayload } from '../../../services/coachService';
import Input from '../../../components/common/Input';
import Textarea from '../../../components/common/Textarea';
import Button from '../../../components/common/Button';

const profileCompletionSchema = z.object({
  location: z.string().min(1, "Location is required."),
  bio: z.string().min(10, "Please provide a brief bio.").optional().nullable(),
  phone_number: z.string().optional().nullable(),
  public_contact_email: z.string().email("Must be a valid email.").optional().or(z.literal('')),
  niche_focus_areas: z.string().optional().nullable(),
  languages_spoken: z.string().optional().nullable(),
  years_of_experience: z.coerce.number().min(0, "Years of experience cannot be negative.").optional().nullable(),
  introductory_video_url: z.string().url("Must be a valid URL.").or(z.literal('')).optional().nullable(),
  linkedin_profile_url: z.string().url("Must be a valid URL.").or(z.literal('')).optional().nullable(),
});

type FormInputs = z.infer<typeof profileCompletionSchema>;

interface CoachProfileCompletionFormProps {
  onSubmit: (data: CoachProfileUpdatePayload) => void;
  onSkip: () => void;
  isLoading: boolean;
}

const CoachProfileCompletionForm: React.FC<CoachProfileCompletionFormProps> = ({ onSubmit, onSkip, isLoading }) => {
  const { register, handleSubmit, formState: { errors } } = useForm<FormInputs>({
    resolver: zodResolver(profileCompletionSchema),
  });

  const handleFormSubmit = (data: FormInputs) => {
    const payload: CoachProfileUpdatePayload = {
      ...data,
      niche_focus_areas: data.niche_focus_areas ? data.niche_focus_areas.split(',').map(s => s.trim()).filter(Boolean) : [],
      languages_spoken: data.languages_spoken ? data.languages_spoken.split(',').map(s => s.trim()).filter(Boolean) : [],
      social_media_links: data.linkedin_profile_url ? { linkedin: data.linkedin_profile_url } : null,
    };
    onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <Input label="Location (e.g., City, Country)" {...register('location')} error={errors.location?.message} />
      <Textarea label="Bio" {...register('bio')} error={errors.bio?.message} rows={4} placeholder="Tell potential clients about your coaching philosophy and approach." />
      <Input label="Phone Number (Optional)" {...register('phone_number')} error={errors.phone_number?.message} />
      <Input label="Public Contact Email (Optional)" {...register('public_contact_email')} error={errors.public_contact_email?.message} />
      <Input label="Niche / Focus Areas (comma-separated)" {...register('niche_focus_areas')} error={errors.niche_focus_areas?.message} placeholder="e.g., Executive Coaching, Career Transitions" />
      <Input label="Languages Spoken (comma-separated)" {...register('languages_spoken')} error={errors.languages_spoken?.message} placeholder="e.g., English, Spanish" />
      <Input label="Years of Experience (Optional)" type="number" {...register('years_of_experience')} error={errors.years_of_experience?.message} />
      <Input label="Intro Video URL (e.g., YouTube, Vimeo)" {...register('introductory_video_url')} error={errors.introductory_video_url?.message} />
      <Input label="LinkedIn Profile URL (Optional)" {...register('linkedin_profile_url')} error={errors.linkedin_profile_url?.message} />
      
      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onSkip} disabled={isLoading}>Skip for Now</Button>
        <Button type="submit" isLoading={isLoading}>Complete Profile</Button>
      </div>
    </form>
  );
};

export default CoachProfileCompletionForm;